import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useReducer,
} from 'react';
import { Prescripteur } from '@europrocurement/l2d-domain';
import { DataProviderProps, DataState } from './types';

export type TiersAddress = {
    adresse1: string;
    adresse2: string;
    adresse3: string;
    adresse4: string;
    codePostal: string;
    ville: string;
    fullAddress: string;
};

export type TiersContact = {
    id: number;
    lastName: string;
    firstName: string;
    email?: string;
    phone?: string;
    accounting: boolean;
    admin: boolean;
    main: boolean;
    userIdentifier?: string;
};

export type Tiers = {
    id: number;
    raisonSociale: string;
    siren: string;
    codeComptable?: string;
    adresse: TiersAddress;
    contacts: TiersContact[];
};

type TiersState = DataState & {
    fetching: Array<{ tiersId: number; fetching: boolean }>;
    data: Array<Tiers>;
};

export const apiToStateTransformer = (item: Prescripteur, idSociete: number): Tiers => {
    const defaultAddress = item.adresses.find((addr) => addr.defaut);

    return {
        id: item.id as number,
        raisonSociale: item.raisonSociale as string,
        siren: item.siren as string,
        codeComptable: item.relationsSocietes.find((relSoc) => relSoc.societe?.id === idSociete)
            ?.codeClient,
        adresse: {
            adresse1: defaultAddress?.adresse1 as string,
            adresse2: defaultAddress?.adresse2 as string,
            adresse3: defaultAddress?.adresse3 as string,
            adresse4: defaultAddress?.adresse4 as string,
            codePostal: defaultAddress?.codePostal as string,
            ville: defaultAddress?.ville as string,
            fullAddress: [
                defaultAddress?.adresse1,
                defaultAddress?.adresse2,
                defaultAddress?.adresse3,
                defaultAddress?.adresse4,
                defaultAddress?.codePostal,
                defaultAddress?.ville,
            ]
                .filter((s) => s)
                .join(', '),
        },
        contacts: item.contacts.map((contact) => ({
            id: contact.id as number,
            lastName: contact.nomContact,
            firstName: contact.prenomContact,
            email: contact.emailsContact.find((mail) => mail.defaut)?.email,
            phone: contact.telephonesContact.find((ph) => ph.defaut)?.telephone,
            accounting: contact.comptabilite as boolean,
            admin: contact.administrateur as boolean,
            main: contact.principal as boolean,
            userIdentifier: contact.userIdentifier,
        })),
    };
};

export const ADD_TIERS_CTX = 'add_tiers_ctx';
export const START_FETCH_TIERS_CTX = 'start_fetch_tiers_ctx';
export const STOP_FETCH_TIERS_CTX = 'stop_fetch_tiers_ctx';
export const ERASE_FETCH_TIERS_CTX = 'erase_fetch_tiers_ctx';

type Action =
    | { type: 'init_tiers_ctx'; payload: Array<Tiers> }
    | { type: 'add_tiers_ctx'; payload: Tiers }
    | { type: 'start_fetch_tiers_ctx'; payload: number }
    | { type: 'stop_fetch_tiers_ctx'; payload: number };

export const TiersContext = createContext<TiersState | null>(null);
export const TiersDispatchContext = createContext<React.Dispatch<Action> | null>(null);

function tiersReducer(state: TiersState, action: Action) {
    switch (action.type) {
        case ADD_TIERS_CTX: {
            return {
                ...state,
                data: [...state.data.filter((t) => t.id !== action.payload.id), action.payload],
            };
        }
        case START_FETCH_TIERS_CTX: {
            return {
                ...state,
                fetching: [
                    ...state.fetching.filter((f) => f.tiersId !== action.payload),
                    { tiersId: action.payload, fetching: true },
                ],
            };
        }
        case STOP_FETCH_TIERS_CTX: {
            return {
                ...state,
                fetching: [
                    ...state.fetching.filter((f) => f.tiersId !== action.payload),
                    { tiersId: action.payload, fetching: false },
                ],
            };
        }
        default: {
            throw new Error(`Unknown action: ${action.type}`);
        }
    }
}

export const TiersProvider: React.FunctionComponent<PropsWithChildren<DataProviderProps>> =
    function ({ children }) {
        const [state, dispatch] = useReducer(tiersReducer, {
            loaded: true,
            fetching: [],
            data: [],
        });

        return (
            <TiersContext.Provider value={state}>
                <TiersDispatchContext.Provider value={dispatch}>
                    {children}
                </TiersDispatchContext.Provider>
            </TiersContext.Provider>
        );
    };

export function useTiersCtxDispatch() {
    return useContext(TiersDispatchContext);
}

export function useTiersCtx() {
    const state = useContext(TiersContext);

    const getTiers = useCallback((): Array<Tiers> => state?.data || [], [state?.data]);

    const getTiersById = useCallback(
        (id: number): Tiers | undefined => (state?.data || []).find((item) => item.id === id),
        [state?.data],
    );

    const getMultipleTiersFiltered = useCallback(
        (filters: Partial<Record<keyof Tiers, unknown>>): Array<Tiers> =>
            (state?.data || []).filter((item) =>
                Object.entries(filters).every(([key, value]) => item[key as keyof Tiers] === value),
            ),
        [state?.data],
    );

    const getTiersFiltered = useCallback(
        (filters: Partial<Record<keyof Tiers, unknown>>): Tiers | undefined =>
            (state?.data || []).find((item) =>
                Object.entries(filters).every(([key, value]) => item[key as keyof Tiers] === value),
            ),
        [state?.data],
    );

    const isFetch = useCallback(
        (tiersId: number): boolean | undefined => {
            const fetching = state?.fetching.find((f) => f.tiersId === tiersId);

            if (fetching) {
                return !fetching.fetching;
            }

            return false;
        },
        [state?.fetching],
    );

    return {
        getTiers,
        getTiersById,
        getMultipleTiersFiltered,
        getTiersFiltered,
        isFetch,
    };
}
